import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { formatTicketPriority, formatTicketStatus, toJsDate } from '../../../features/support-tickets/utils/ticketFormatters';
import { adminListSupportTickets } from '../services/adminTicketingFunctionsApi';
import { adminSupportTicketPath } from '../../constants/adminPaths';
import { playNewChatSound } from '../../utils/notificationSounds';
import PaginationBar from '../../../shared/components/pagination/PaginationBar';
import { PageSkeleton } from '../../../components/loading/skeletons';

import '../styles/adminTicketing.css';

function statusBadgeClass(status) {
  const classes = ['at-badge', 'at-badgeStatus'];
  if (status === 'open') classes.push('at-badgeOpen');
  if (status === 'in_progress') classes.push('at-badgeInProgress');
  if (status === 'resolved') classes.push('at-badgeResolved');
  if (status === 'closed') classes.push('at-badgeClosed');
  return classes.join(' ');
}

function priorityBadgeClass(priority) {
  const classes = ['at-badge', 'at-badgePriority'];
  if (priority === 'low') classes.push('at-badgeLow');
  if (priority === 'normal') classes.push('at-badgeNormal');
  if (priority === 'high') classes.push('at-badgeHigh');
  if (priority === 'urgent') classes.push('at-badgeUrgent');
  return classes.join(' ');
}

function safeText(v) {
  return typeof v === 'string' ? v : '';
}

export default function AdminTicketingListPage() {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const getAdminIdTokenRef = useRef(null);
  const ticketsSigRef = useRef('');
  const ticketsInFlightRef = useRef(false);
  const seenTicketKeysRef = useRef(new Set());
  const hasLoadedOnceRef = useRef(false);

  const [statusFilter, setStatusFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [tickets, setTickets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);

  const PAGE_SIZE = 10;

  useEffect(() => {
    getAdminIdTokenRef.current = admin?.getIdToken;
  }, [admin?.getIdToken]);

  const computeSig = (items) =>
    Array.isArray(items)
      ? items
          .map((t) => {
            const sec = t?.updatedAt?.seconds ?? t?.updatedAt?._seconds ?? null;
            return `${t.companyId || ''}:${t.id || ''}:${sec || ''}:${t.status || ''}:${t.priority || ''}`;
          })
          .join('|')
      : '';

  const refresh = async () => {
    if (ticketsInFlightRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

    ticketsInFlightRef.current = true;
    setError('');
    setLoading(true);
    try {
      const data = await adminListSupportTickets({ status: 'all', pageSize: 200 }, getAdminIdTokenRef.current);
      const items = Array.isArray(data) ? data : [];

      // Play sound when *new* tickets appear (not on initial load)
      try {
        const nextKeys = new Set(items.map((t) => `${t?.companyId || ''}:${t?.id || ''}`));
        if (hasLoadedOnceRef.current) {
          let newCount = 0;
          for (const k of nextKeys) {
            if (k && !seenTicketKeysRef.current.has(k)) newCount += 1;
          }
          if (newCount > 0) {
            playNewChatSound();
          }
        }
        seenTicketKeysRef.current = nextKeys;
        hasLoadedOnceRef.current = true;
      } catch (soundErr) {
        // Never block refresh on sound issues (autoplay, etc.)
        console.log('Ticket sound check failed:', soundErr);
      }

      const sig = computeSig(items);
      if (sig !== ticketsSigRef.current) {
        ticketsSigRef.current = sig;
        setTickets(items);
      }
    } catch (e) {
      console.error(e);
      setError('Could not load tickets.');
    } finally {
      setLoading(false);
      ticketsInFlightRef.current = false;
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 120000);
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener?.('visibilitychange', onVis);
    window.addEventListener?.('focus', refresh);
    return () => {
      clearInterval(t);
      document.removeEventListener?.('visibilitychange', onVis);
      window.removeEventListener?.('focus', refresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusCounts = useMemo(() => {
    const base = { open: 0, in_progress: 0, resolved: 0, closed: 0, all: 0 };
    if (!tickets) return base;
    return tickets.reduce((acc, t) => {
      acc.all += 1;
      if (t.status === 'open') acc.open += 1;
      if (t.status === 'in_progress') acc.in_progress += 1;
      if (t.status === 'resolved') acc.resolved += 1;
      if (t.status === 'closed') acc.closed += 1;
      return acc;
    }, base);
  }, [tickets]);

  const filtered = useMemo(() => {
    if (!tickets) return null;
    const q = search.trim().toLowerCase();
    const byStatus = statusFilter === 'all' ? tickets : tickets.filter((t) => t.status === statusFilter);
    if (!q) return byStatus;
    return byStatus.filter((t) => {
      const subject = safeText(t.subject).toLowerCase();
      const companyName = safeText(t.companyName).toLowerCase();
      const email = safeText(t.createdBy?.email).toLowerCase();
      const uid = safeText(t.createdBy?.uid).toLowerCase();
      return (
        subject.includes(q) ||
        companyName.includes(q) ||
        email.includes(q) ||
        uid.includes(q) ||
        safeText(t.companyId).toLowerCase().includes(q)
      );
    });
  }, [tickets, search, statusFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, search]);

  const paged = useMemo(() => {
    if (!filtered) return null;
    const start = page * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  // Clamp page if results shrink
  useEffect(() => {
    const total = filtered?.length || 0;
    const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > pageCount - 1) setPage(pageCount - 1);
  }, [filtered?.length, page]);

  const openTicket = (t) => {
    if (!t?.companyId || !t?.id) return;
    navigate(adminSupportTicketPath(t.companyId, t.id), { state: { ticket: t } });
  };

  return (
    <div className="admin-page">
      <div className="at-hero">
        <h2 className="at-heroTitle">Support Tickets</h2>
        <p className="at-heroSub">Inbox view (click a ticket to open the thread).</p>
      </div>

      <div className="at-card" style={{ marginTop: 14 }}>
        <div className="at-cardHeader">
          <h3 className="at-cardTitle">Inbox</h3>
          {loading ? <div className="at-loader" aria-label="Loading" /> : <span className="at-pill">{filtered?.length || 0}</span>}
        </div>

        <div className="at-tabs" role="tablist" aria-label="Ticket status tabs">
          {[
            { key: 'open', label: 'Open', count: statusCounts.open },
            { key: 'in_progress', label: 'In progress', count: statusCounts.in_progress },
            { key: 'resolved', label: 'Resolved', count: statusCounts.resolved },
            { key: 'closed', label: 'Closed', count: statusCounts.closed },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              className={`at-tab at-tab${t.key === 'in_progress' ? 'InProgress' : t.key.charAt(0).toUpperCase() + t.key.slice(1)} ${statusFilter === t.key ? 'at-tabActive' : ''}`}
              onClick={() => setStatusFilter(t.key)}
            >
              <span>{t.label}</span>
              <span className="at-tabCount">{t.count}</span>
            </button>
          ))}
          <button
            type="button"
            className={`at-tab ${statusFilter === 'all' ? 'at-tabActive' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            <span>All</span>
            <span className="at-tabCount">{statusCounts.all}</span>
          </button>
        </div>

        <div className="at-controls">
          <div className="at-row2">
            <div>
              <label className="at-label" htmlFor="at-search">Search</label>
              <input
                id="at-search"
                className="at-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Subject, company, email, uid…"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="at-label">Refresh</label>
              <button type="button" className="at-btn" onClick={refresh} disabled={loading}>
                Refresh inbox
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="at-alert at-alertError">{error}</div> : null}

        {loading ? (
          <PageSkeleton showHeader={false} variant="table" rows={8} label="Loading tickets" />
        ) : !filtered || filtered.length === 0 ? (
          <div className="at-empty">No tickets found.</div>
        ) : (
          <div className="at-list">
            {(paged || []).map((t) => {
              const dt = toJsDate(t.updatedAt || t.createdAt);
              const timeText = dt ? dt.toLocaleString() : '—';
              const companyName = t.companyName || t.companyId || '—';
              const fromText = t.createdBy?.email || t.createdBy?.uid || '—';
              const assigneeNameRaw = t.assignedTo?.name || t.assignedTo?.email || '';
              const assigneeName = typeof assigneeNameRaw === 'string' ? assigneeNameRaw.split(' - ')[0] : '';
              const ticketNumberText =
                typeof t.ticketNumber === 'number'
                  ? `Ticket #${t.ticketNumber}`
                  : (typeof t.ticketNumber === 'string' && t.ticketNumber.trim())
                    ? `Ticket #${t.ticketNumber.trim()}`
                    : `Ticket #${String(t.id || '').slice(0, 6).toUpperCase()}`;

              return (
                <div
                  key={`${t.companyId || 'unknown'}-${t.id}`}
                  className="at-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => openTicket(t)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openTicket(t);
                  }}
                >
                  <div className="at-itemTop">
                    <div className="at-itemSubject">{t.subject || '(no subject)'}</div>
                    <span className="at-badgeRow">
                      <span className={statusBadgeClass(t.status)}>{formatTicketStatus(t.status)}</span>
                      <span className={priorityBadgeClass(t.priority)}>{formatTicketPriority(t.priority)}</span>
                    </span>
                  </div>
                  <div className="at-itemMeta">
                    <span title={t.id ? `Internal ID: ${t.id}` : undefined}>{ticketNumberText}</span>
                    <span title={companyName}>Company: {companyName}</span>
                    <span title={fromText}>From: {fromText}</span>
                    <span title={assigneeNameRaw}>{assigneeName ? `Assignee: ${assigneeName}` : 'Assignee: Unassigned'}</span>
                    <span title={timeText}>Updated: {timeText}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && (filtered?.length || 0) > PAGE_SIZE ? (
          <PaginationBar
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered?.length || 0}
            onPageChange={setPage}
            ariaLabel="Tickets pagination"
          />
        ) : null}
      </div>
    </div>
  );
}

